import { IOpenVesselDefinitionV1 } from "open-vessel-definition";
import { ISlOption } from "@tedivo/tedivo-form";
import { applyOvdToStafOptionsToData } from "open-vessel-definition/build/src/converter/core/cleanup/applyOvdToStafOptionsToData";
import { cloneObject } from "@tedivo/tedivo-pure-helpers";
import { getTranslation } from "../../../../../../../app/i18/i18tn";
import ovdJsonStore from "../../../../../../../app/stores/OVDJsonStore";

/** The CGs treatment the user can choose before exporting (STAF and NSD). */
export type TAddRemoveCgs = "none" | "remove" | "addArtificial";

/**
 * Artificial CGs can only be offered when the file actually misses some of the
 * important ones, otherwise there is nothing to invent.
 */
export function canAddArtificialCgs(): boolean {
  const lcgVcgTcgAndPairings = ovdJsonStore.lcgVcgTcgAndPairings;

  return (
    !!lcgVcgTcgAndPairings?.missingImportantVcgs || !!lcgVcgTcgAndPairings?.missingImportantLcgs
  );
}

/**
 * The 3 CGs options shown in the export dialogs. The "add artificial" one is
 * dropped when there are no missing CGs to fill in.
 */
export function createCgsOptions(withArtificialCgs: boolean): ISlOption[] {
  return [
    {
      value: "none",
      label: getTranslation("general:exportToStaf.keepIntactCgs"),
    },
    {
      value: "remove",
      label: getTranslation("general:exportToStaf.removeCgs"),
    },
    withArtificialCgs
      ? {
          value: "addArtificial",
          label: getTranslation("general:exportToStaf.addArtificialCgs"),
        }
      : undefined,
  ].filter(Boolean) as ISlOption[];
}

/**
 * Returns the OVD to export, with the chosen CGs treatment applied.
 *
 * "none" returns the very same object; "remove" and "addArtificial" always
 * return a clone, so the store's json is never touched.
 */
export function applyCgsOptionToOvd(
  json: IOpenVesselDefinitionV1,
  addRemoveCgs: TAddRemoveCgs,
): IOpenVesselDefinitionV1 {
  if (addRemoveCgs === "remove") {
    // applyOvdToStafOptionsToData mutates what it receives, hence the clone.
    return applyOvdToStafOptionsToData(cloneObject(json), {
      removeCGs: true,
      removeBaysWithNonSizeSlots: false,
      removeBelowTiers24AndHigher: false,
    });
  }

  if (addRemoveCgs === "addArtificial") {
    const lcgVcgTcgAndPairings = ovdJsonStore.lcgVcgTcgAndPairings;

    if (!lcgVcgTcgAndPairings || !canAddArtificialCgs()) return json;

    return lcgVcgTcgAndPairings.addArtificialCgsToJson(
      json,
      ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
    );
  }

  return json;
}
